import { useMemo } from 'react';
import { unwrapBundle } from '@lifeomic/phc-web-toolkit/src/util/fhir';
import type { ReviewableResourceType } from '@lifeomic/phc-web-toolkit/src/util/extractedResourceReview';
import { useDataset, useFHIRQuery } from '../../../../util/api';
import {
  DOCUMENT_EXTRACTION_V2_PROJECTION_IDENTIFIER_SYSTEM,
  getDocumentExtractionV2ReviewState,
} from './ExtractedResourceProvenance';

export type { ReviewableResourceType };

type CodeableConcept = {
  text?: string;
  coding?: Array<{ display?: string; code?: string }>;
};

type Quantity = {
  value?: number;
  unit?: string;
  code?: string;
};

type Annotation = {
  text?: string;
};

type DosageTiming = {
  code?: CodeableConcept;
  repeat?: {
    frequency?: number;
    period?: number;
    periodUnit?: string;
    when?: string[];
    timeOfDay?: string[];
  };
};

// FHIR Dosage (MedicationStatement) / DosageInstruction (MedicationRequest).
type Dosage = {
  text?: string;
  patientInstruction?: string;
  doseQuantity?: Quantity;
  route?: CodeableConcept;
  timing?: DosageTiming;
};

export type ExtractedFhirResource = fhir.Resource & {
  id?: string;
  code?: CodeableConcept;
  medicationCodeableConcept?: CodeableConcept;
  medicationReference?: { display?: string };
  vaccineCode?: CodeableConcept;
  extension?: fhir.Extension[];
  identifier?: Array<{ system?: string; value?: string }>;
  // Medication dosage / timing / notes. MedicationStatement carries `dosage`,
  // MedicationRequest carries `dosageInstruction`; free-text clinician notes
  // live in `note[]`.
  dosage?: Dosage[];
  dosageInstruction?: Dosage[];
  note?: Annotation[];
  // Observation value[x] variants.
  valueQuantity?: Quantity;
  valueString?: string;
  valueInteger?: number;
  valueBoolean?: boolean;
  valueCodeableConcept?: CodeableConcept;
  valueRange?: { low?: Quantity; high?: Quantity };
  component?: Array<{
    code?: CodeableConcept;
    valueQuantity?: Quantity;
    valueString?: string;
    valueCodeableConcept?: CodeableConcept;
  }>;
  // Clinically relevant date[x] variants across resource types.
  effectiveDateTime?: string;
  effectivePeriod?: { start?: string; end?: string };
  issued?: string;
  onsetDateTime?: string;
  assertedDate?: string;
  recordedDate?: string;
  performedDateTime?: string;
  performedPeriod?: { start?: string; end?: string };
  date?: string;
  // MedicationRequest records when the order was written; it has no
  // effective[x].
  authoredOn?: string;
};

export type UnreviewedExtractedResource = {
  id: string;
  resourceType: ReviewableResourceType;
  display: string;
  resource: ExtractedFhirResource;
};

const DOCUMENT_EXTRACTION_V2_IDENTIFIER_TOKEN = `${DOCUMENT_EXTRACTION_V2_PROJECTION_IDENTIFIER_SYSTEM}|`;

const getCodeableConcept = (
  resource: ExtractedFhirResource,
): CodeableConcept | undefined =>
  resource.code ?? resource.medicationCodeableConcept ?? resource.vaccineCode;

/**
 * Resolve a human-readable Code/Display label for any extracted clinical
 * resource, regardless of which `CodeableConcept` field the resource type uses.
 */
export const getExtractedResourceDisplay = (
  resource: ExtractedFhirResource,
): string => {
  const codeable = getCodeableConcept(resource);
  return (
    codeable?.coding?.find(coding => coding.display)?.display ||
    codeable?.text ||
    resource.medicationReference?.display ||
    codeable?.coding?.[0]?.code ||
    '—'
  );
};

const formatQuantity = (quantity: Quantity | undefined): string | undefined => {
  if (quantity?.value == null) {
    return undefined;
  }
  const unit = quantity.unit ?? quantity.code;
  return unit ? `${quantity.value} ${unit}` : `${quantity.value}`;
};

const codeableConceptLabel = (
  concept: CodeableConcept | undefined,
): string | undefined =>
  concept?.coding?.find(coding => coding.display)?.display ||
  concept?.text ||
  concept?.coding?.[0]?.code ||
  undefined;

/**
 * Resolve a human-readable measured value for an extracted Observation,
 * covering the common FHIR `value[x]` shapes (quantity, codeable concept,
 * string, range, and multi-component results such as blood pressure). Returns
 * `undefined` when the resource carries no value, so callers can omit the
 * field for resource types that do not have one.
 */
export const getExtractedObservationValue = (
  resource: ExtractedFhirResource,
): string | undefined => {
  const quantity = formatQuantity(resource.valueQuantity);
  if (quantity) {
    return quantity;
  }
  if (resource.valueString) {
    return resource.valueString;
  }
  if (typeof resource.valueInteger === 'number') {
    return `${resource.valueInteger}`;
  }
  if (typeof resource.valueBoolean === 'boolean') {
    return resource.valueBoolean ? 'Yes' : 'No';
  }
  const codeableValue = codeableConceptLabel(resource.valueCodeableConcept);
  if (codeableValue) {
    return codeableValue;
  }
  if (resource.valueRange) {
    const low = formatQuantity(resource.valueRange.low);
    const high = formatQuantity(resource.valueRange.high);
    if (low && high) {
      return `${low} – ${high}`;
    }
    return low ?? high ?? undefined;
  }
  if (resource.component?.length) {
    const parts = resource.component
      .map(
        component =>
          formatQuantity(component.valueQuantity) ||
          component.valueString ||
          codeableConceptLabel(component.valueCodeableConcept),
      )
      .filter((part): part is string => Boolean(part));
    if (parts.length > 0) {
      return parts.join(' / ');
    }
  }
  return undefined;
};

// Human-readable singular labels for FHIR timing period units (UCUM).
const PERIOD_UNIT_LABELS: Record<string, string> = {
  s: 'second',
  min: 'minute',
  h: 'hour',
  d: 'day',
  wk: 'week',
  mo: 'month',
  a: 'year',
};

/**
 * Render a FHIR dosage timing as a short, human-readable schedule, preferring
 * the free-text `timing.code.text` and otherwise summarizing the structured
 * `timing.repeat` (frequency/period, time-of-day, and event codes such as
 * `AC`/`HS`). Returns `undefined` when the timing carries no usable schedule.
 */
const formatTiming = (timing: DosageTiming | undefined): string | undefined => {
  if (!timing) {
    return undefined;
  }
  if (timing.code?.text) {
    return timing.code.text;
  }
  const repeat = timing.repeat;
  if (!repeat) {
    return undefined;
  }
  const parts: string[] = [];
  if (repeat.frequency != null && repeat.period != null) {
    const unit = repeat.periodUnit
      ? PERIOD_UNIT_LABELS[repeat.periodUnit] ?? repeat.periodUnit
      : undefined;
    const pluralUnit =
      unit && repeat.period !== 1 ? `${repeat.period} ${unit}s` : unit;
    parts.push(
      pluralUnit
        ? `${repeat.frequency}× per ${pluralUnit}`
        : `${repeat.frequency}×`,
    );
  }
  if (repeat.when?.length) {
    parts.push(repeat.when.join(', '));
  }
  if (repeat.timeOfDay?.length) {
    parts.push(`at ${repeat.timeOfDay.join(', ')}`);
  }
  return parts.length > 0 ? parts.join(' · ') : undefined;
};

export type ExtractedMedicationDetails = {
  dosage?: string;
  dose?: string;
  timing?: string;
  route?: string;
  notes?: string;
};

/**
 * Resolve the additional medication-specific detail surfaced when reviewing an
 * extracted `MedicationStatement`/`MedicationRequest`: dosage instructions,
 * dose quantity, timing/schedule, route, and free-text notes. Each field is
 * `undefined` when not present so callers can omit it. Notes combine the
 * resource `note[]` text with any dosage `patientInstruction`.
 */
export const getExtractedMedicationDetails = (
  resource: ExtractedFhirResource,
): ExtractedMedicationDetails => {
  const dosage = resource.dosage?.[0] ?? resource.dosageInstruction?.[0];
  const noteParts = [
    ...(resource.note ?? []).map(note => note?.text),
    dosage?.patientInstruction,
  ].filter((part): part is string => Boolean(part));
  return {
    dosage: dosage?.text || undefined,
    dose: formatQuantity(dosage?.doseQuantity),
    timing: formatTiming(dosage?.timing),
    route: dosage?.route ? codeableConceptLabel(dosage.route) : undefined,
    notes: noteParts.length > 0 ? noteParts.join(' · ') : undefined,
  };
};

/**
 * Resolve the clinically relevant date for an extracted resource, preferring
 * the effective/observation date and falling back to other date[x] fields used
 * across resource types. Returns the raw FHIR date string (callers format it
 * for display), or `undefined` when none is present.
 */
export const getExtractedResourceDate = (
  resource: ExtractedFhirResource,
): string | undefined =>
  resource.effectiveDateTime ||
  resource.effectivePeriod?.start ||
  resource.issued ||
  resource.onsetDateTime ||
  resource.performedDateTime ||
  resource.performedPeriod?.start ||
  resource.date ||
  resource.authoredOn ||
  resource.assertedDate ||
  resource.recordedDate ||
  undefined;

/**
 * Flatten extracted resources (grouped by type) into the subset that still
 * needs clinician review, with a display label resolved for each.
 */
export const buildUnreviewedExtractedResources = (
  groups: Array<{
    resourceType: ReviewableResourceType;
    resources: ExtractedFhirResource[];
  }>,
): UnreviewedExtractedResource[] =>
  groups.flatMap(({ resourceType, resources }) =>
    resources
      .filter(
        resource =>
          getDocumentExtractionV2ReviewState(resource) === 'needs-review',
      )
      .map(resource => ({
        id: resource.id ?? '',
        resourceType,
        display: getExtractedResourceDisplay(resource),
        resource,
      })),
  );

/**
 * Fetches the document-extraction-v2 clinical resources for a member that
 * still need clinician review (conditions, labs, medications, etc.), so the
 * Zori Pulse page can surface them alongside detected issues.
 */
export const useUnreviewedExtractedResources = (
  memberId: string,
): { resources: UnreviewedExtractedResource[]; isLoading: boolean } => {
  const dataset = useDataset();
  const enabled = Boolean(memberId) && Boolean(dataset.codingToken);

  const params = {
    patient: memberId,
    _tag: dataset.codingToken,
    identifier: DOCUMENT_EXTRACTION_V2_IDENTIFIER_TOKEN,
    pageSize: 100,
  };
  const options = {
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: false,
  };

  const conditions = useFHIRQuery(
    'GET /v1/fhir/dstu3/Condition',
    params,
    options,
  );
  const observations = useFHIRQuery(
    'GET /v1/fhir/dstu3/Observation',
    params,
    options,
  );
  const medications = useFHIRQuery(
    'GET /v1/fhir/dstu3/MedicationStatement',
    params,
    options,
  );
  // Extraction projects prescription-shaped medications (an order with an
  // intent and prescriber) as MedicationRequest, so both medication resource
  // types have to be pulled into the review queue.
  const medicationRequests = useFHIRQuery(
    'GET /v1/fhir/dstu3/MedicationRequest',
    params,
    options,
  );
  const allergies = useFHIRQuery(
    'GET /v1/fhir/dstu3/AllergyIntolerance',
    params,
    options,
  );
  const immunizations = useFHIRQuery(
    'GET /v1/fhir/dstu3/Immunization',
    params,
    options,
  );
  const procedures = useFHIRQuery(
    'GET /v1/fhir/dstu3/Procedure',
    params,
    options,
  );

  const resources = useMemo(
    () =>
      buildUnreviewedExtractedResources([
        {
          resourceType: 'Condition',
          resources: unwrapBundle(conditions.data) as ExtractedFhirResource[],
        },
        {
          resourceType: 'Observation',
          resources: unwrapBundle(observations.data) as ExtractedFhirResource[],
        },
        {
          resourceType: 'MedicationStatement',
          resources: unwrapBundle(medications.data) as ExtractedFhirResource[],
        },
        {
          resourceType: 'MedicationRequest',
          resources: unwrapBundle(
            medicationRequests.data,
          ) as ExtractedFhirResource[],
        },
        {
          resourceType: 'AllergyIntolerance',
          resources: unwrapBundle(allergies.data) as ExtractedFhirResource[],
        },
        {
          resourceType: 'Immunization',
          resources: unwrapBundle(
            immunizations.data,
          ) as ExtractedFhirResource[],
        },
        {
          resourceType: 'Procedure',
          resources: unwrapBundle(procedures.data) as ExtractedFhirResource[],
        },
      ]),
    [
      conditions.data,
      observations.data,
      medications.data,
      medicationRequests.data,
      allergies.data,
      immunizations.data,
      procedures.data,
    ],
  );

  const isLoading =
    conditions.isLoading ||
    observations.isLoading ||
    medications.isLoading ||
    medicationRequests.isLoading ||
    allergies.isLoading ||
    immunizations.isLoading ||
    procedures.isLoading;

  return { resources, isLoading };
};
